import {
  collection,
  doc,
  writeBatch,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  increment,
  arrayUnion,
  runTransaction
} from "firebase/firestore";
import { db } from "../../../firebase/firestore.js";
import { auth } from "../../../firebase/auth.js";
import { writeAudit } from "../../../firebase/auditLogger.js";

const PAYMENTS_COLLECTION = "payments";
const INVOICES_COLLECTION = "invoices";
const PURCHASES_COLLECTION = "purchases";
const CUSTOMERS_COLLECTION = "customers";
const BANK_TXN_COLLECTION = "bankTransactions";

/** Fields that return a bank statement line to the "To match" queue. */
export const UNMATCHED_FIELDS = {
  matchStatus: "unmatched",
  matchType: null,
  paymentId: null,
  paymentNo: null,
  partyId: null,
  partyName: null,
  category: null,
  matchedBy: null,
  matchedAt: null
};

/**
 * Atomically records a lump-sum customer payment and allocates it across multiple invoices (FIFO or custom).
 * Updates each affected invoice, creates an audit payment document, and updates customer outstanding ledger.
 */
export async function recordCustomerPaymentTally({
  customerId,
  customerName,
  customerPhone = "",
  companyId = null,
  amount,
  totalApplied,
  unallocatedAmount = 0,
  paymentDate,
  paymentMode = "Cash",
  reference = "",
  notes = "",
  allocations = [],
  bankTxnId = null,
  bankMatch = null
}) {
  const activeAllocations = allocations.filter((a) => Number(a.allocatedAmount || 0) > 0);
  const receivedAmount = Number(amount || 0);
  const appliedAmount = Number(totalApplied || 0);

  if (!customerId) {
    throw new Error("Customer is required to record payment.");
  }
  if (receivedAmount <= 0) {
    throw new Error("Payment amount received must be greater than zero.");
  }
  if (activeAllocations.length === 0 && appliedAmount <= 0 && unallocatedAmount <= 0) {
    throw new Error("No invoices selected or payment allocated.");
  }

  const batch = writeBatch(db);
  const user = auth.currentUser?.email || auth.currentUser?.uid || "admin";

  // 1. Create Payment Document (PAYMENT_IN)
  const paymentRef = doc(collection(db, PAYMENTS_COLLECTION));
  const paymentNo = `RCP-${Date.now().toString().slice(-6)}`;

  const paymentDocData = {
    paymentNo,
    type: "PAYMENT_IN",
    customerId,
    customerName: customerName || "Customer",
    customerPhone: customerPhone || "",
    partyId: customerId,
    partyName: customerName || "Customer",
    companyId: companyId || null,
    amount: receivedAmount,
    totalApplied: appliedAmount,
    unallocatedAmount: Math.max(0, Number(unallocatedAmount || 0)),
    paymentDate: paymentDate || new Date().toISOString().split("T")[0],
    paymentMode: paymentMode || "Cash",
    reference: reference ? reference.trim() : "",
    notes: notes ? notes.trim() : "",
    allocations: activeAllocations.map((a) => ({
      invoiceId: a.invoiceId,
      invoiceNo: a.invoiceNo,
      invoiceDate: a.invoiceDate || "",
      grandTotal: Number(a.grandTotal || 0),
      balanceDue: Number(a.balanceDue || 0),
      allocatedAmount: Number(a.allocatedAmount || 0),
      remainingBalance: Number(a.remainingBalance || 0),
      nextStatus: a.nextStatus
    })),
    ...(bankTxnId ? { bankTxnId, source: "bank" } : {}),
    createdBy: user,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  batch.set(paymentRef, paymentDocData);
  // Recorded from a bank statement line: mark that line matched in the same commit
  if (bankTxnId) {
    batch.update(doc(db, BANK_TXN_COLLECTION, bankTxnId), {
      ...(bankMatch || {}),
      matchStatus: "matched",
      paymentId: paymentRef.id,
      paymentNo,
      matchedBy: user,
      matchedAt: serverTimestamp()
    });
  }

  // 2. Update each affected Invoice document
  for (const alloc of activeAllocations) {
    const invoiceRef = doc(db, INVOICES_COLLECTION, alloc.invoiceId);
    const amt = Number(alloc.allocatedAmount || 0);
    const nextStatus = alloc.remainingBalance === 0 ? "Paid" : "Partial";

    batch.update(invoiceRef, {
      paid: increment(amt),
      balance: increment(-amt),
      paymentStatus: nextStatus,
      paymentHistory: arrayUnion({
        paymentId: paymentRef.id,
        paymentNo,
        amount: amt,
        paymentDate: paymentDocData.paymentDate,
        paymentMode: paymentDocData.paymentMode,
        reference: paymentDocData.reference,
        createdAt: new Date().toISOString()
      }),
      updatedAt: serverTimestamp()
    });
  }

  // 3. Update Customer document (decrement total outstanding balance by total applied)
  if (customerId && appliedAmount > 0) {
    const custRef = doc(db, CUSTOMERS_COLLECTION, customerId);
    batch.update(custRef, {
      openingBalance: increment(-appliedAmount),
      updatedAt: serverTimestamp()
    });
  }

  // Commit all writes atomically
  let commitErr = null;
  try {
    await batch.commit();
  } catch (err) {
    commitErr = err;
    // Audit the failure before rethrowing
    writeAudit("PAYMENT_IN_FAILED", {
      paymentNo,
      customerId,
      customerName,
      amount: receivedAmount,
      totalApplied: appliedAmount,
      allocationsCount: activeAllocations.length,
      paymentDate,
      paymentMode
    }, "ERROR", err?.message);
    throw err;
  }

  // Audit the successful payment (fire-and-forget)
  writeAudit("PAYMENT_IN_RECORDED", {
    paymentId: paymentRef.id,
    paymentNo,
    customerId,
    customerName,
    amount: receivedAmount,
    totalApplied: appliedAmount,
    unallocatedAmount: paymentDocData.unallocatedAmount,
    allocationsCount: activeAllocations.length,
    paymentDate,
    paymentMode,
    reference
  });

  return {
    paymentId: paymentRef.id,
    paymentNo,
    totalApplied: appliedAmount,
    unallocatedAmount: paymentDocData.unallocatedAmount,
    allocationsCount: activeAllocations.length,
    paymentRecord: {
      id: paymentRef.id,
      ...paymentDocData,
      createdAt: new Date().toISOString()
    }
  };
}

/**
 * Atomically records a lump-sum payment made to a vendor/supplier across multiple purchase bills.
 * Updates each affected purchase bill, records a payment receipt (PAYMENT_OUT), and decrements vendor payable balance.
 */
export async function recordVendorPaymentTally({
  vendorId,
  vendorName,
  vendorPhone = "",
  companyId = null,
  amount,
  totalApplied,
  unallocatedAmount = 0,
  paymentDate,
  paymentMode = "Bank Transfer / NEFT",
  reference = "",
  notes = "",
  allocations = [],
  bankTxnId = null,
  bankMatch = null
}) {
  const activeAllocations = allocations.filter((a) => Number(a.allocatedAmount || 0) > 0);
  const paidAmount = Number(amount || 0);
  const appliedAmount = Number(totalApplied || 0);

  if (!vendorId) {
    throw new Error("Vendor is required to record payment.");
  }
  if (paidAmount <= 0) {
    throw new Error("Payment amount paid must be greater than zero.");
  }
  if (activeAllocations.length === 0 && appliedAmount <= 0 && unallocatedAmount <= 0) {
    throw new Error("No purchase bills selected or payment allocated.");
  }

  const batch = writeBatch(db);
  const user = auth.currentUser?.email || auth.currentUser?.uid || "admin";

  // 1. Create Payment Document (PAYMENT_OUT)
  const paymentRef = doc(collection(db, PAYMENTS_COLLECTION));
  const paymentNo = `VEND-${Date.now().toString().slice(-6)}`;

  const paymentDocData = {
    paymentNo,
    type: "PAYMENT_OUT",
    vendorId,
    vendorName: vendorName || "Vendor",
    vendorPhone: vendorPhone || "",
    partyId: vendorId,
    partyName: vendorName || "Vendor",
    companyId: companyId || null,
    amount: paidAmount,
    totalApplied: appliedAmount,
    unallocatedAmount: Math.max(0, Number(unallocatedAmount || 0)),
    paymentDate: paymentDate || new Date().toISOString().split("T")[0],
    paymentMode: paymentMode || "Bank Transfer / NEFT",
    reference: reference ? reference.trim() : "",
    notes: notes ? notes.trim() : "",
    allocations: activeAllocations.map((a) => ({
      purchaseId: a.purchaseId || a.invoiceId,
      billNo: a.billNo || a.vendorBillNo || a.invoiceNo,
      billDate: a.billDate || a.invoiceDate || "",
      grandTotal: Number(a.grandTotal || 0),
      balanceDue: Number(a.balanceDue || 0),
      allocatedAmount: Number(a.allocatedAmount || 0),
      remainingBalance: Number(a.remainingBalance || 0),
      nextStatus: a.nextStatus
    })),
    ...(bankTxnId ? { bankTxnId, source: "bank" } : {}),
    createdBy: user,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  batch.set(paymentRef, paymentDocData);
  // Recorded from a bank statement line: mark that line matched in the same commit
  if (bankTxnId) {
    batch.update(doc(db, BANK_TXN_COLLECTION, bankTxnId), {
      ...(bankMatch || {}),
      matchStatus: "matched",
      paymentId: paymentRef.id,
      paymentNo,
      matchedBy: user,
      matchedAt: serverTimestamp()
    });
  }

  // 2. Update each affected Purchase Bill document
  for (const alloc of activeAllocations) {
    const targetBillId = alloc.purchaseId || alloc.invoiceId;
    const billRef = doc(db, PURCHASES_COLLECTION, targetBillId);
    const amt = Number(alloc.allocatedAmount || 0);
    const nextStatus = alloc.remainingBalance === 0 ? "Paid" : "Partial";

    batch.update(billRef, {
      paid: increment(amt),
      balance: increment(-amt),
      paymentStatus: nextStatus,
      paymentHistory: arrayUnion({
        paymentId: paymentRef.id,
        paymentNo,
        amount: amt,
        paymentDate: paymentDocData.paymentDate,
        paymentMode: paymentDocData.paymentMode,
        reference: paymentDocData.reference,
        createdAt: new Date().toISOString()
      }),
      updatedAt: serverTimestamp()
    });
  }

  // 3. Update Vendor document in customers collection (reduce payable balance)
  if (vendorId && appliedAmount > 0) {
    const vendorRef = doc(db, CUSTOMERS_COLLECTION, vendorId);
    batch.update(vendorRef, {
      openingBalance: increment(-appliedAmount),
      updatedAt: serverTimestamp()
    });
  }

  // Commit all writes atomically
  try {
    await batch.commit();
  } catch (err) {
    writeAudit("PAYMENT_OUT_FAILED", {
      paymentNo,
      vendorId,
      vendorName,
      amount: paidAmount,
      totalApplied: appliedAmount,
      allocationsCount: activeAllocations.length,
      paymentDate,
      paymentMode
    }, "ERROR", err?.message);
    throw err;
  }

  // Audit the successful vendor payment (fire-and-forget)
  writeAudit("PAYMENT_OUT_RECORDED", {
    paymentId: paymentRef.id,
    paymentNo,
    vendorId,
    vendorName,
    amount: paidAmount,
    totalApplied: appliedAmount,
    unallocatedAmount: paymentDocData.unallocatedAmount,
    allocationsCount: activeAllocations.length,
    paymentDate,
    paymentMode,
    reference
  });

  return {
    paymentId: paymentRef.id,
    paymentNo,
    totalApplied: appliedAmount,
    unallocatedAmount: paymentDocData.unallocatedAmount,
    allocationsCount: activeAllocations.length,
    paymentRecord: {
      id: paymentRef.id,
      ...paymentDocData,
      createdAt: new Date().toISOString()
    }
  };
}

/**
 * Fetch all payments with optional filtering by type ('PAYMENT_IN' | 'PAYMENT_OUT') or partyId
 */
export async function getPayments({ type = null, partyId = null, limitCount = 100 } = {}) {
  // A single party's history must be complete, not a slice of the latest N payments overall
  if (partyId) {
    const list = await getPartyPayments(partyId);
    return type ? list.filter((p) => p.type === type) : list;
  }

  const colRef = collection(db, PAYMENTS_COLLECTION);
  let docs = [];

  try {
    const constraints = [orderBy("createdAt", "desc"), limit(limitCount)];
    const q = query(colRef, ...constraints);
    const snapshot = await getDocs(q);
    docs = snapshot.docs;
  } catch (err) {
    console.warn("Ordered payment query failed, falling back to full collection scan:", err);
    const snapshot = await getDocs(colRef);
    docs = snapshot.docs;
  }

  const list = docs.map((d) => {
    const data = d.data();
    let createdAtIso = "";
    if (data.createdAt?.toDate) {
      createdAtIso = data.createdAt.toDate().toISOString();
    } else if (typeof data.createdAt === "string") {
      createdAtIso = data.createdAt;
    } else if (data.paymentDate) {
      createdAtIso = `${data.paymentDate}T00:00:00.000Z`;
    }

    return {
      id: d.id,
      ...data,
      createdAt: createdAtIso
    };
  });

  return list
    .filter((p) => {
      if (type && p.type !== type) return false;
      if (partyId && p.partyId !== partyId && p.customerId !== partyId && p.vendorId !== partyId) return false;
      return true;
    })
    .sort((a, b) => {
      const aDate = a.createdAt || a.paymentDate || "";
      const bDate = b.createdAt || b.paymentDate || "";
      return bDate.localeCompare(aDate);
    });
}

/** Normalises a payment doc (createdAt as ISO string). */
function toPaymentRecord(d) {
  const data = d.data();
  let createdAtIso = "";
  if (data.createdAt?.toDate) createdAtIso = data.createdAt.toDate().toISOString();
  else if (typeof data.createdAt === "string") createdAtIso = data.createdAt;
  else if (data.paymentDate) createdAtIso = `${data.paymentDate}T00:00:00.000Z`;
  return { id: d.id, ...data, createdAt: createdAtIso };
}

/**
 * All payments for one party (customer or vendor), newest first.
 * Single-field equality query — no composite index needed.
 */
export async function getPartyPayments(partyId) {
  if (!partyId) return [];
  const snapshot = await getDocs(query(collection(db, PAYMENTS_COLLECTION), where("partyId", "==", partyId)));
  return snapshot.docs
    .map(toPaymentRecord)
    .sort((a, b) => String(b.paymentDate || b.createdAt || "").localeCompare(String(a.paymentDate || a.createdAt || "")));
}

/**
 * Fetch a single payment receipt by ID
 */
export async function getPaymentById(paymentId) {
  if (!paymentId) return null;
  const docRef = doc(db, PAYMENTS_COLLECTION, paymentId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;

  const data = snap.data();
  return {
    id: snap.id,
    ...data,
    createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt
  };
}

/* ------------------------------------------------------------------ */
/* Payment correction: void, edit, and reset unrecorded payments       */
/* ------------------------------------------------------------------ */

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

/** Bill collection + allocation key for a payment direction. */
function getBillConfig(type) {
  return type === "PAYMENT_OUT"
    ? { billCollection: PURCHASES_COLLECTION, billIdOf: (a) => a.purchaseId || a.invoiceId, label: "PAYMENT_OUT" }
    : { billCollection: INVOICES_COLLECTION, billIdOf: (a) => a.invoiceId, label: "PAYMENT_IN" };
}

/** Recompute payment status from stored totals (±1 paisa tolerance). */
export function computePaymentStatus(grandTotal, paid) {
  const total = Number(grandTotal || 0);
  const paidAmt = Number(paid || 0);
  if (paidAmt <= 0.01) return "Unpaid";
  if (paidAmt >= total - 0.01) return "Paid";
  return "Partial";
}

/** Same stored shape as recordCustomerPaymentTally / recordVendorPaymentTally. */
function buildStoredAllocations(type, allocations) {
  return allocations.map((a) => (type === "PAYMENT_OUT"
    ? {
      purchaseId: a.purchaseId || a.invoiceId,
      billNo: a.billNo || a.vendorBillNo || a.invoiceNo,
      billDate: a.billDate || a.invoiceDate || "",
      grandTotal: Number(a.grandTotal || 0),
      balanceDue: Number(a.balanceDue || 0),
      allocatedAmount: Number(a.allocatedAmount || 0),
      remainingBalance: Number(a.remainingBalance || 0),
      nextStatus: a.nextStatus
    }
    : {
      invoiceId: a.invoiceId,
      invoiceNo: a.invoiceNo,
      invoiceDate: a.invoiceDate || "",
      grandTotal: Number(a.grandTotal || 0),
      balanceDue: Number(a.balanceDue || 0),
      allocatedAmount: Number(a.allocatedAmount || 0),
      remainingBalance: Number(a.remainingBalance || 0),
      nextStatus: a.nextStatus
    }));
}

/** Sum allocated amount per bill id (a payment could list a bill once, but be defensive). */
function sumByBill(allocations, billIdOf) {
  const map = new Map();
  allocations.forEach((a) => {
    const id = billIdOf(a);
    const amt = Number(a.allocatedAmount || 0);
    if (id && amt > 0) map.set(id, round2((map.get(id) || 0) + amt));
  });
  return map;
}

/**
 * Voids a recorded payment: restores each allocated bill's paid/balance/status,
 * marks its paymentHistory entry as voided, restores the party's outstanding balance,
 * and flags the payment document as VOID (it is kept for audit, never deleted).
 */
export async function voidPayment(paymentId, reason) {
  if (!paymentId) throw new Error("Payment is required.");
  const voidReason = String(reason || "").trim();
  if (!voidReason) throw new Error("Please give a reason for voiding this payment.");

  const user = auth.currentUser?.email || auth.currentUser?.uid || "admin";
  const paymentRef = doc(db, PAYMENTS_COLLECTION, paymentId);
  let summary = null;

  try {
    summary = await runTransaction(db, async (tx) => {
      const paySnap = await tx.get(paymentRef);
      if (!paySnap.exists()) throw new Error("Payment not found.");
      const payment = paySnap.data();
      if (payment.status === "VOID") throw new Error(`Payment ${payment.paymentNo} is already void.`);

      const { billCollection, billIdOf, label } = getBillConfig(payment.type);
      const amountsByBill = sumByBill(payment.allocations || [], billIdOf);

      // Reads first (Firestore transactions require all reads before writes)
      const billSnaps = [];
      for (const billId of amountsByBill.keys()) {
        billSnaps.push({ billId, snap: await tx.get(doc(db, billCollection, billId)) });
      }
      const bankSnap = payment.bankTxnId ? await tx.get(doc(db, BANK_TXN_COLLECTION, payment.bankTxnId)) : null;

      const missingBills = [];
      let restoredToParty = 0;
      for (const { billId, snap } of billSnaps) {
        const amt = amountsByBill.get(billId);
        if (!snap.exists()) {
          missingBills.push(billId);
          continue;
        }
        const bill = snap.data();
        const grandTotal = Number(bill.grandTotal || 0);
        const nextPaid = round2(Math.max(0, Number(bill.paid || 0) - amt));
        tx.update(snap.ref, {
          paid: nextPaid,
          balance: round2(Math.max(0, grandTotal - nextPaid)),
          paymentStatus: computePaymentStatus(grandTotal, nextPaid),
          paymentHistory: (bill.paymentHistory || []).map((h) => (
            h.paymentId === paymentId ? { ...h, voided: true, voidReason } : h
          )),
          updatedAt: serverTimestamp()
        });
        restoredToParty = round2(restoredToParty + amt);
      }

      const partyId = payment.partyId || payment.customerId || payment.vendorId;
      if (partyId && restoredToParty > 0) {
        tx.update(doc(db, CUSTOMERS_COLLECTION, partyId), {
          openingBalance: increment(restoredToParty),
          updatedAt: serverTimestamp()
        });
      }

      // A bank statement line tied to this payment goes back to "To match"
      if (bankSnap?.exists() && bankSnap.data().paymentId === paymentId) {
        tx.update(bankSnap.ref, { ...UNMATCHED_FIELDS });
      }

      tx.update(paymentRef, {
        status: "VOID",
        voidReason,
        voidedBy: user,
        voidedAt: new Date().toISOString(),
        updatedAt: serverTimestamp()
      });

      return { label, paymentNo: payment.paymentNo, partyId, amount: payment.amount, restoredToParty, missingBills };
    });
  } catch (err) {
    writeAudit("PAYMENT_VOID_FAILED", { paymentId, voidReason }, "ERROR", err?.message);
    throw err;
  }

  writeAudit(`${summary.label}_VOIDED`, {
    paymentId,
    paymentNo: summary.paymentNo,
    partyId: summary.partyId,
    amount: summary.amount,
    restoredToParty: summary.restoredToParty,
    missingBillsCount: summary.missingBills.length,
    voidReason
  });

  return summary;
}

/**
 * Edits a recorded payment in place (same receipt number). Reverses the old allocations
 * and applies the new ones atomically, adjusting the party balance by the difference.
 */
export async function updatePayment(paymentId, {
  amount,
  totalApplied,
  unallocatedAmount = 0,
  paymentDate,
  paymentMode,
  reference = "",
  notes = "",
  allocations = []
}) {
  if (!paymentId) throw new Error("Payment is required.");
  const newAmount = round2(amount);
  const newApplied = round2(totalApplied);
  if (newAmount <= 0) throw new Error("Payment amount must be greater than zero.");

  const user = auth.currentUser?.email || auth.currentUser?.uid || "admin";
  const paymentRef = doc(db, PAYMENTS_COLLECTION, paymentId);
  let summary = null;

  try {
    summary = await runTransaction(db, async (tx) => {
      const paySnap = await tx.get(paymentRef);
      if (!paySnap.exists()) throw new Error("Payment not found.");
      const payment = paySnap.data();
      if (payment.status === "VOID") throw new Error(`Payment ${payment.paymentNo} is void and cannot be edited.`);

      const { billCollection, billIdOf, label } = getBillConfig(payment.type);
      const activeAllocations = allocations.filter((a) => Number(a.allocatedAmount || 0) > 0);
      const oldByBill = sumByBill(payment.allocations || [], billIdOf);
      const newByBill = sumByBill(activeAllocations, billIdOf);
      const billIds = [...new Set([...oldByBill.keys(), ...newByBill.keys()])];

      const billSnaps = new Map();
      for (const billId of billIds) {
        billSnaps.set(billId, await tx.get(doc(db, billCollection, billId)));
      }

      const nextDate = paymentDate || payment.paymentDate;
      const nextMode = paymentMode || payment.paymentMode;
      const nextReference = reference ? reference.trim() : "";
      let oldAppliedExisting = 0;

      for (const billId of billIds) {
        const snap = billSnaps.get(billId);
        const oldAmt = oldByBill.get(billId) || 0;
        const newAmt = newByBill.get(billId) || 0;
        if (!snap.exists()) {
          if (newAmt > 0) throw new Error("One of the selected bills no longer exists. Please reopen and try again.");
          continue; // old allocation on a deleted bill — nothing to reverse
        }
        oldAppliedExisting = round2(oldAppliedExisting + oldAmt);

        const bill = snap.data();
        const grandTotal = Number(bill.grandTotal || 0);
        const paidWithoutThis = round2(Math.max(0, Number(bill.paid || 0) - oldAmt));
        const balanceWithoutThis = round2(Math.max(0, grandTotal - paidWithoutThis));
        if (newAmt > balanceWithoutThis + 0.01) {
          throw new Error(`₹${newAmt} is more than the ₹${balanceWithoutThis} due on bill ${bill.invoiceNo || bill.vendorBillNo || billId}.`);
        }
        const nextPaid = round2(paidWithoutThis + newAmt);

        const history = (bill.paymentHistory || []).filter((h) => h.paymentId !== paymentId);
        if (newAmt > 0) {
          const previous = (bill.paymentHistory || []).find((h) => h.paymentId === paymentId);
          history.push({
            paymentId,
            paymentNo: payment.paymentNo,
            amount: newAmt,
            paymentDate: nextDate,
            paymentMode: nextMode,
            reference: nextReference,
            createdAt: previous?.createdAt || new Date().toISOString(),
            editedAt: new Date().toISOString()
          });
        }

        tx.update(snap.ref, {
          paid: nextPaid,
          balance: round2(Math.max(0, grandTotal - nextPaid)),
          paymentStatus: computePaymentStatus(grandTotal, nextPaid),
          paymentHistory: history,
          updatedAt: serverTimestamp()
        });
      }

      const partyId = payment.partyId || payment.customerId || payment.vendorId;
      const partyDelta = round2(oldAppliedExisting - newApplied);
      if (partyId && partyDelta !== 0) {
        tx.update(doc(db, CUSTOMERS_COLLECTION, partyId), {
          openingBalance: increment(partyDelta),
          updatedAt: serverTimestamp()
        });
      }

      tx.update(paymentRef, {
        amount: newAmount,
        totalApplied: newApplied,
        unallocatedAmount: round2(Math.max(0, Number(unallocatedAmount || 0))),
        paymentDate: nextDate,
        paymentMode: nextMode,
        reference: nextReference,
        notes: notes ? notes.trim() : "",
        allocations: buildStoredAllocations(payment.type, activeAllocations),
        editedAt: new Date().toISOString(),
        editedBy: user,
        editCount: increment(1),
        updatedAt: serverTimestamp()
      });

      return { label, paymentNo: payment.paymentNo, partyId, oldAmount: payment.amount, oldApplied: payment.totalApplied };
    });
  } catch (err) {
    writeAudit("PAYMENT_UPDATE_FAILED", { paymentId, amount: newAmount }, "ERROR", err?.message);
    throw err;
  }

  writeAudit(`${summary.label}_UPDATED`, {
    paymentId,
    paymentNo: summary.paymentNo,
    partyId: summary.partyId,
    oldAmount: summary.oldAmount,
    newAmount,
    oldApplied: summary.oldApplied,
    newApplied,
    paymentDate,
    paymentMode
  });

  return getPaymentById(paymentId);
}

/** Paid amount on a bill that has no matching (non-void) payment record. */
export function getUnrecordedPaidAmount(bill) {
  if (!bill) return 0;
  const recorded = (bill.paymentHistory || [])
    .filter((h) => !h.voided)
    .reduce((sum, h) => sum + Number(h.amount || 0), 0);
  const paid = Number(bill.paid !== undefined ? bill.paid : (bill.paidAmount || 0));
  return round2(Math.max(0, paid - recorded));
}

/**
 * Removes a payment amount on a bill that was never recorded as a payment
 * (older inline "Record Payment" entries, or bills saved as Paid at creation).
 * billType: 'invoice' | 'purchase'
 */
export async function resetUnrecordedPayment(billType, billId) {
  const billCollection = billType === "purchase" ? PURCHASES_COLLECTION : INVOICES_COLLECTION;
  const billRef = doc(db, billCollection, billId);
  let summary = null;

  try {
    summary = await runTransaction(db, async (tx) => {
      const snap = await tx.get(billRef);
      if (!snap.exists()) throw new Error("Bill not found.");
      const bill = snap.data();
      const unrecorded = getUnrecordedPaidAmount(bill);
      if (unrecorded <= 0) throw new Error("There is no unrecorded payment on this bill.");

      const grandTotal = Number(bill.grandTotal || 0);
      const nextPaid = round2(Math.max(0, Number(bill.paid || 0) - unrecorded));
      tx.update(billRef, {
        paid: nextPaid,
        balance: round2(Math.max(0, grandTotal - nextPaid)),
        paymentStatus: computePaymentStatus(grandTotal, nextPaid),
        updatedAt: serverTimestamp()
      });

      const partyId = billType === "purchase" ? bill.vendorId : bill.customerId;
      if (partyId) {
        tx.update(doc(db, CUSTOMERS_COLLECTION, partyId), {
          openingBalance: increment(unrecorded),
          updatedAt: serverTimestamp()
        });
      }

      return { unrecorded, partyId, billNo: bill.invoiceNo || bill.vendorBillNo };
    });
  } catch (err) {
    writeAudit("UNRECORDED_PAYMENT_RESET_FAILED", { billType, billId }, "ERROR", err?.message);
    throw err;
  }

  writeAudit("UNRECORDED_PAYMENT_RESET", { billType, billId, ...summary });
  return summary;
}
